import { useCallback, useMemo } from 'react';
import { TextSelect, Eye, Pencil } from 'lucide-react';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { EditorSelectionRange } from '@/types/aiWorkspace';
import type { DatabaseEngineId } from '@/core/engines';
import { isExecutableSelect, isLikelyCompleteStatement } from '../../common/utils/peekResultsDetection';
import type { VqeUnavailableReason } from '../../visual-query-editor/compact/vqeEditAffordance';
import { VqeUnavailableIndicator } from '../../visual-query-editor/compact/VqeUnavailableIndicator';
import { useSmartBarPlacement } from '../hooks/useSmartBarPlacement';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface CursorStatementActionsProps {
  /** Ref to the SqlMonaco editor handle (for pixel-position + scroll subscription). */
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  /** The full editor content — drives statement parsing. */
  editorContent: string;
  /** The source range of the statement under the cursor (null = nothing to show). */
  statementRange: EditorSelectionRange | null;
  /** The trimmed statement text (used to gate Execute/Peek visibility). */
  statementText: string;
  /** Active engine — threads through to the engine-aware completeness heuristic. */
  engineId?: DatabaseEngineId;
  /** Whether the main query is currently executing. */
  isExecuting: boolean;
  /** Whether a peek query is currently executing. */
  isPeekExecuting?: boolean;
  /**
   * Whether the Execute / Peek buttons may render. These stay 2+-statement-only
   * (single-statement buffers run via the toolbar / F5). The edit pencil is NOT
   * gated on this — it renders for the cursor statement in every buffer. Defaults
   * to `true` so existing call sites / tests keep their Execute/Peek behavior.
   */
  showRunActions?: boolean;
  /** Run just the cursor's statement. */
  onExecute?: (statementSql: string) => void;
  /**
   * Peek (inline preview) the cursor's statement. `anchorLine` tells the
   * Peek overlay where to pin itself — we pass the statement's end line so
   * the overlay attaches below the full highlighted statement, even though
   * there's no active text selection for the hook to derive an anchor from.
   */
  onPeek?: (statementSql: string, anchorLine?: number) => void;
  /**
   * When true, the cursor statement is VQE-editable (SELECT/DML or DDL) so a
   * small `[✎] Edit` button renders next to Execute / Peek.
   */
  canEdit?: boolean;
  /** Open the minimal edit affordance (compact popover / full DDL editor). */
  onEdit?: () => void;
  /**
   * Why the cursor statement can't be edited visually, when the pencil is
   * hidden (procedural / mixed set-op / genuine parse-error). Non-null renders
   * a passive teaching indicator in place of the pencil — the never-silent
   * honesty floor (VQE Polish). Mutually exclusive with `canEdit`.
   */
  unavailableReason?: VqeUnavailableReason;
}

// Shared with SelectionFloatingActions so both bars align visually.
// Buttons "glue" to the statement by overlapping the anchor edge by 2px —
// the glued look with negative gap visually tucks the bar under the
// highlight's rounded edge. `BUTTON_HEIGHT_WITH_GAP` = (button height 22)
// + (gap -2), giving the above-placement its pixel offset in one constant.
const BUTTON_HEIGHT_WITH_GAP = 20;
const BUTTON_BELOW_GAP = -2;

/**
 * Floating Execute|Peek bar anchored to the statement under the cursor when
 * the editor has multiple statements. Sister component to
 * `SelectionFloatingActions` — that one fires on explicit text selection;
 * this one fires on cursor-only placement so users can run the statement
 * they're typing/editing without selecting it first.
 *
 * All placement logic (above/below/spacer fallback) lives in
 * `useSmartBarPlacement` and is shared with the selection bar.
 */
export function CursorStatementActions({
  editorRef,
  editorContent,
  statementRange,
  statementText,
  engineId,
  isExecuting,
  isPeekExecuting,
  showRunActions = true,
  onExecute,
  onPeek,
  canEdit = false,
  onEdit,
  unavailableReason = null,
}: CursorStatementActionsProps) {
  // The Execute button requires a "likely complete" statement — stricter
  // than `isExecutableStatement`. This suppresses the bar while the user is
  // still typing (trailing `FROM`, unbalanced parens, trailing operator, …).
  const looksComplete = useMemo(
    () => !!statementText && isLikelyCompleteStatement(statementText, engineId),
    [statementText, engineId],
  );
  // Execute / Peek are 2+-statement-only (`showRunActions`). On a single-statement
  // buffer the pencil still renders, but the run controls do not — those run via
  // the toolbar / F5.
  const canExecute = showRunActions && looksComplete;
  // Peek additionally requires the statement be a safe, read-only SELECT-shape.
  const canPeek = useMemo(
    () => showRunActions && looksComplete && isExecutableSelect(statementText),
    [showRunActions, looksComplete, statementText],
  );
  const showEdit = canEdit && !!onEdit;
  // Never-silent: an unavailable statement shows a passive teaching indicator in
  // place of the pencil (they are mutually exclusive — a reason only exists when
  // the pencil is hidden).
  const showUnavailable = !!unavailableReason && !showEdit;
  // The bar mounts when there's any run action, an edit pencil, OR a teaching
  // indicator to show.
  const hasAnyAction = canExecute || canPeek || showEdit || showUnavailable;

  const position = useSmartBarPlacement({
    editorRef,
    editorContent,
    range: statementRange,
    enabled: hasAnyAction && !isExecuting,
    buttonHeightWithGap: BUTTON_HEIGHT_WITH_GAP,
    belowGap: BUTTON_BELOW_GAP,
  });

  const handleExecuteClick = useCallback(() => {
    if (statementText && onExecute) onExecute(statementText);
  }, [statementText, onExecute]);

  const handlePeekClick = useCallback(() => {
    if (statementText && onPeek && !isPeekExecuting) {
      onPeek(statementText, statementRange?.endLineNumber);
    }
  }, [statementText, onPeek, isPeekExecuting, statementRange]);

  const handleEditClick = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  if (!position || !hasAnyAction) return null;

  // Per-corner rounding: the first visible button rounds its left corner, the
  // last rounds its right corner. Edit (when shown) is always last.
  const above = position.mode === 'above';
  const leftClass = above ? 'rounded-tl-md' : 'rounded-bl-md';
  const rightClass = above ? 'rounded-tr-md' : 'rounded-br-md';
  const fullClass = above ? 'rounded-t-md' : 'rounded-b-md';
  const onlyOne = [canExecute, canPeek, showEdit].filter(Boolean).length === 1;
  const executeRounding = onlyOne ? fullClass : leftClass;
  const peekIsLast = canPeek && !showEdit;
  const peekRounding = onlyOne
    ? fullClass
    : !canExecute && !peekIsLast
      ? leftClass
      : peekIsLast
        ? rightClass
        : '';
  const editRounding = onlyOne ? fullClass : rightClass;

  // Fade the 2px edge that touches the statement highlight so the bar
  // reads as "growing out" of the highlighted statement rather than
  // sitting on top of it. The gradient goes from opaque (interior) →
  // transparent (touching edge), letting the highlight's tint bleed
  // through the last 2px of the button.
  const edgeMask =
    position.mode === 'above'
      ? 'linear-gradient(to bottom, black, black calc(100% - 2px), transparent)'
      : 'linear-gradient(to bottom, transparent, black 2px, black)';

  return (
    <div
      className="absolute z-20 pointer-events-auto flex items-stretch gap-0"
      style={{
        top: position.top,
        left: position.left,
        maskImage: edgeMask,
        WebkitMaskImage: edgeMask,
      }}
      data-testid="cursor-statement-actions"
    >
      {canExecute && (
        <button
          onClick={handleExecuteClick}
          // Fill is `--primary-alt` (U10) — matches the query-toolbar
          // Execute button and the gutter Execute button
          // (CursorStatementGutterActions), all sharing one grey fill token.
          // See src/index.css's --primary-alt comment and task-U10-report.md.
          //
          // U15 — full-strength `bg-primary-alt` (not `/85`), matching the
          // toolbar exactly. Hover is PINNED to `/90` — not omitted — per
          // this wave's recurring Tailwind-specificity trap (a scoped
          // `hover:` utility silently outranks a plain state class); see
          // QueryToolbar.tsx's Execute button comment.
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-primary-alt text-primary-alt-foreground shadow-md
            hover:bg-primary-alt/90 transition-colors backdrop-blur-sm
            border border-primary-alt/20 ${executeRounding}`}
          data-testid="cursor-statement-execute-button"
          title="Execute this statement"
        >
          {/* U15 — orange glyph, fill-aware not theme-aware: matches the
              toolbar's `text-energy-on-dark-fill` / `dark:text-energy-on-
              light-fill` pair. See QueryToolbar.tsx and src/index.css's
              --energy-on-*-fill tokens for the measured writeup. */}
          <TextSelect className="h-3 w-3 text-energy-on-dark-fill dark:text-energy-on-light-fill" />
          Execute
        </button>
      )}

      {canPeek && (
        <button
          onClick={handlePeekClick}
          disabled={isPeekExecuting}
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-background/85 text-foreground shadow-md
            hover:bg-accent transition-colors backdrop-blur-sm
            border border-border disabled:opacity-70 ${peekRounding}`}
          data-testid="cursor-statement-peek-button"
          title="Peek this statement's results"
        >
          {isPeekExecuting ? (
            <RandomDotmSpinner size={12} />
          ) : (
            <Eye className="h-3 w-3" />
          )}
          {isPeekExecuting ? 'Peeking...' : 'Peek'}
        </button>
      )}

      {showEdit && (
        <button
          onClick={handleEditClick}
          className={`flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium
            bg-background/85 text-foreground shadow-md
            hover:bg-accent transition-colors backdrop-blur-sm
            border border-border ${editRounding}`}
          data-testid="vqe-edit-inline-button"
          title="Edit this statement visually"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
      )}

      {/* Never-silent teaching indicator — replaces the pencil when the cursor
          statement can't be edited visually (procedural / mixed set-op /
          genuine parse-error). Passive; the tooltip names construct + reason +
          next step. */}
      {showUnavailable && unavailableReason && (
        <VqeUnavailableIndicator reason={unavailableReason} variant="inline" />
      )}
    </div>
  );
}
